import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { cmsApi } from '../api';
import { useCity } from '../context/CityContext';
import { useAuth } from '../context/AuthContext';
import './ZenGuide.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'zg_chat_v1';
const TTL_MS      = 2 * 60 * 60 * 1000; // 2 hours

const OPENING_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm **Zén Guide** 🌿\n\nAsk me for recommendations on where to eat, drink, or what to do — tell me your vibe, budget, or area and I'll find the perfect spot for you.",
};

// ── Entity card ───────────────────────────────────────────────────────────────
function EntityCard({ entity, type }) {
  const href = `/${type}/${entity.slug}`;

  let price = null;
  if (type === 'places' && (entity.avg_spend ?? 0) > 0)
    price = `₦${Number(entity.avg_spend).toLocaleString()} avg`;
  else if (type === 'events')
    price = (entity.ticket_price ?? 0) > 0
      ? `₦${Number(entity.ticket_price).toLocaleString()}`
      : 'Free entry';
  else if (type === 'activities')
    price = (entity.base_price ?? 0) > 0
      ? `₦${Number(entity.base_price).toLocaleString()}`
      : 'Free';

  const location = [entity.area, entity.city].filter(Boolean).join(', ');

  const date =
    type === 'events' && entity.date
      ? new Date(entity.date).toLocaleDateString('en-NG', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        })
      : null;

  const rawAbout  = entity.about ? entity.about.replace(/<[^>]+>/g, '').trim() : '';
  const snippet   = rawAbout.length > 0 ? rawAbout.slice(0, 90) + (rawAbout.length > 90 ? '…' : '') : null;

  return (
    <a href={href} className="zg-card">
      <div className="zg-card-top">
        {entity.category && <span className="zg-card-badge">{entity.category}</span>}
        {price && <span className="zg-card-price">{price}</span>}
      </div>
      <div className="zg-card-name">{entity.name}</div>
      {(location || date) && (
        <div className="zg-card-meta">
          {location && <span>{location}</span>}
          {date && <span> · {date}</span>}
        </div>
      )}
      {snippet && <div className="zg-card-snippet">{snippet}</div>}
    </a>
  );
}

// ── Text renderer ─────────────────────────────────────────────────────────────
/**
 * Renders assistant text.
 * Lines matching "→ /type/slug" are swapped for EntityCard components.
 * All other lines support **bold** and line-break formatting.
 */
function AssistantText({ text, entities = {} }) {
  const lines = text.split('\n');

  return (
    <div className="zg-body">
      {lines.map((line, i) => {
        // Match → /places/slug  or  → https://domain/places/slug
        const linkMatch = line.match(/→\s*(?:https?:\/\/[^/]+)?(\/([^/\s]+)\/([^\s)]+))/);
        if (linkMatch) {
          const path = linkMatch[1];
          const type = linkMatch[2];                        // 'places' | 'events' | 'activities'
          const slug = linkMatch[3].replace(/\W+$/, '');   // strip trailing punctuation
          const entity = (entities[type] || []).find(e => e.slug === slug);
          if (entity) return <EntityCard key={i} entity={entity} type={type} />;
          // Fallback when entity data not found
          return (
            <a key={i} href={path} className="zg-result-link">
              View listing
            </a>
          );
        }

        // Empty line → small gap
        if (!line.trim()) return <div key={i} className="zg-spacer" />;

        // Regular text with **bold** support
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} className="zg-line">
            {parts.map((p, j) =>
              p.startsWith('**') && p.endsWith('**')
                ? <strong key={j}>{p.slice(2, -2)}</strong>
                : p
            )}
          </p>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ZenGuide() {
  const { selectedCity } = useCity();
  const { user }         = useAuth(); // eslint-disable-line no-unused-vars

  // Lazy-init from localStorage so there's no flash on first render
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const { messages: saved, ts } = JSON.parse(raw);
        if (Array.isArray(saved) && saved.length > 0 && Date.now() - ts < TTL_MS) {
          return saved;
        }
      }
    } catch {}
    return [OPENING_MESSAGE];
  });

  // Pick 4 random suggestions from a larger pool; refreshes when city changes
  const suggestions = useMemo(() => {
    const city = selectedCity;
    const pool = [
      city ? `Best spots to eat in ${city}` : 'Best restaurants right now',
      city ? `Top rated places in ${city}` : 'Top rated places near me',
      city ? `Fun things to do in ${city} this weekend` : 'Fun things to do this weekend',
      'Chill spots with great vibes',
      'Rooftop bars or outdoor hangouts',
      'Best nightlife spots right now',
      'Where to take a date tonight',
      'Family-friendly activities under ₦5k',
      'Best brunch spots',
      'Hidden gems most people don\'t know about',
      'Outdoor adventures and activities',
      'Where to celebrate a birthday',
      'Spots with good wifi to work from',
      'Live music or events happening soon',
      'Where to go for a quiet evening out',
      'Best local food experiences',
    ];
    return [...pool].sort(() => 0.5 - Math.random()).slice(0, 4);
  }, [selectedCity]);

  const [open,    setOpen]    = useState(false);
  const [input,   setInput]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const panelRef  = useRef(null);

  // Persist messages to localStorage on every change (refresh TTL)
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, ts: Date.now() }));
    } catch {}
  }, [messages]);

  // Scroll to bottom when messages or loading state changes
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        const fab = document.getElementById('zen-guide-fab');
        if (fab && fab.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const sendMessage = useCallback(async (override) => {
    const text = (typeof override === 'string' ? override : input).trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const history = messages
      .filter(m => m !== OPENING_MESSAGE)
      .map(m => ({ role: m.role, content: m.content })); // strip entities from history

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setError('');
    setLoading(true);

    try {
      const data     = await cmsApi.chat(text, selectedCity || '', history);
      const reply    = data?.reply    || "I couldn't find a good match right now — try rephrasing your request!";
      const entities = data?.entities || {};
      setMessages(prev => [...prev, { role: 'assistant', content: reply, entities }]);
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again.');
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, selectedCity]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([OPENING_MESSAGE]);
    setError('');
    setInput('');
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  return (
    <>
      {/* ── Chat panel ── */}
      {open && (
        <div className="zg-panel" ref={panelRef} role="dialog" aria-label="Zén Guide chat">

          {/* Header */}
          <div className="zg-header">
            <div className="zg-header-left">
              <div className="zg-avatar">Z</div>
              <div>
                <div className="zg-name">Zén Guide</div>
                <div className="zg-status">✦ Powered by your city's data</div>
              </div>
            </div>
            <div className="zg-header-actions">
              <button className="zg-icon-btn" onClick={clearChat} title="Clear chat" aria-label="Clear chat">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
                </svg>
              </button>
              <button className="zg-icon-btn" onClick={() => setOpen(false)} title="Close" aria-label="Close chat">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="zg-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`zg-msg zg-msg--${msg.role}`}>
                {msg.role === 'assistant' && <div className="zg-msg-av">Z</div>}
                <div className={`zg-msg-bubble${msg.role === 'assistant' ? ' zg-msg-bubble--assistant' : ''}`}>
                  {msg.role === 'assistant'
                    ? <AssistantText text={msg.content} entities={msg.entities || {}} />
                    : msg.content
                  }
                </div>
              </div>
            ))}

            {loading && (
              <div className="zg-msg zg-msg--assistant">
                <div className="zg-msg-av">Z</div>
                <div className="zg-msg-bubble zg-thinking">
                  <span /><span /><span />
                </div>
              </div>
            )}

            {error && <div className="zg-error">{error}</div>}

            <div ref={bottomRef} />
          </div>

          {/* Suggestion chips (only on opening message) */}
          {messages.length === 1 && !loading && (
            <div className="zg-suggestions">
              {suggestions.map((s) => (
                <button key={s} className="zg-chip" onClick={() => sendMessage(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="zg-input-row">
            <textarea
              ref={inputRef}
              className="zg-input"
              rows={1}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about places, vibes, budget…"
              maxLength={500}
              disabled={loading}
            />
            <button
              className="zg-send"
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              aria-label="Send message"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>

        </div>
      )}

      {/* ── Floating Action Button ── */}
      <button
        id="zen-guide-fab"
        className={`zg-fab ${open ? 'zg-fab--open' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-label="Open Zén Guide"
        title="Zén Guide"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              <path d="M8 10h.01M12 10h.01M16 10h.01" strokeWidth="2.5"/>
            </svg>
            <span className="zg-fab-label">Zén Guide</span>
          </>
        )}
      </button>
    </>
  );
}
